import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import IconButton from "@material-ui/core/IconButton";
import Button from "@material-ui/core/Button";
import CloseIcon from "@material-ui/icons/Close";
import CircularProgress from "@material-ui/core/CircularProgress";
import useTranslation from "Hooks/useTranslation";
import MyIcons from "assets/img/Icons/MyIcons";
import JobPlannerPage from "Pages/JobPlanner/JobPlannerPage";

const ANIMATION_MS = 320;

const JobPlannerSlidePanel = ({ open, onClose, onSelectJob }) => {
  const translation = useTranslation();
  const panelRef = useRef(null);
  const [isRendered, setIsRendered] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const [showContent, setShowContent] = useState(false);
  const [embeddedHeaderActions, setEmbeddedHeaderActions] = useState(null);

  const handleEmbeddedHeaderActions = useCallback((actions) => {
    setEmbeddedHeaderActions(actions);
  }, []);

  useEffect(() => {
    if (open) {
      setShowContent(false);
      setIsRendered(true);
      document.body.style.overflow = "hidden";

      const frame = requestAnimationFrame(() => {
        setIsActive(true);
      });

      return () => cancelAnimationFrame(frame);
    }

    setShowContent(false);
    setEmbeddedHeaderActions(null);
    setIsActive(false);
    document.body.style.overflow = "";

    const timer = window.setTimeout(() => setIsRendered(false), ANIMATION_MS);
    return () => window.clearTimeout(timer);
  }, [open]);

  useEffect(() => {
    if (!isActive || !open || !panelRef.current) return undefined;

    const node = panelRef.current;

    const revealContent = () => {
      setShowContent(true);
    };

    const handleTransitionEnd = (event) => {
      if (event.target !== node || event.propertyName !== "transform") return;
      revealContent();
    };

    const fallback = window.setTimeout(revealContent, ANIMATION_MS + 16);
    node.addEventListener("transitionend", handleTransitionEnd);

    return () => {
      window.clearTimeout(fallback);
      node.removeEventListener("transitionend", handleTransitionEnd);
    };
  }, [isActive, open]);

  if (!isRendered) return null;

  return ReactDOM.createPortal(
    <>
      <div
        className={`jobPlannerSlideBackdrop ${isActive ? "open" : ""}`}
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        ref={panelRef}
        className={`jobPlannerSlidePanel ${isActive ? "open" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label={translation.settingsJobPlannerLabel}
      >
        <div className="jobPlannerSlidePanelHeader">
          <div className="jobPlannerSlidePanelHeaderMain">
            {/* <span className="jobPlannerSlidePanelIcon" aria-hidden="true">
              <MyIcons icon="jobPlannerIcon" />
            </span> */}
            <div className="jobPlannerSlidePanelHeaderText">
              <h2 className="jobPlannerSlidePanelTitle">
                {translation.settingsJobPlannerLabel}
              </h2>
            </div>
          </div>
          <div className="jobPlannerSlidePanelHeaderActions">
            {showContent && embeddedHeaderActions?.hasPendingChanges && (
              <>
                <Button
                  variant="outlined"
                  className="jobPlannerCancelBtn"
                  onClick={embeddedHeaderActions.onCancel}
                  disabled={embeddedHeaderActions.saving}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  className="jobPlannerSaveBtn"
                  onClick={embeddedHeaderActions.onSave}
                  disabled={embeddedHeaderActions.saving}
                >
                  {embeddedHeaderActions.saving ? "Saving…" : "Save"}
                </Button>
              </>
            )}
            {showContent && embeddedHeaderActions && (
              <Button
                className="asideNavItem jobConfigurationAddBtn jobPlannerAddBtn"
                startIcon={<MyIcons icon="addCircleOutlineIcon" />}
                onClick={embeddedHeaderActions.onAdd}
              >
                {embeddedHeaderActions.addLabel}
              </Button>
            )}
            <IconButton
              onClick={onClose}
              aria-label="Close"
              className="jobPlannerSlidePanelClose"
              size="small"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </div>
        </div>
        <div className="jobPlannerSlidePanelContent">
          {showContent ? (
            <JobPlannerPage
              embedded
              onSelectJob={onSelectJob}
              onEmbeddedHeaderActions={handleEmbeddedHeaderActions}
            />
          ) : (
            <div className="jobPlannerSlidePanelLoader">
              <CircularProgress size={28} />
            </div>
          )}
        </div>
      </div>
    </>,
    document.body,
  );
};

export default JobPlannerSlidePanel;
